/**
 * Compressor — isentropic compression with efficiency.
 *
 * h_out = h_in + (h_s(P_out, s_in) - h_in) / η
 * W = ṁ × (h_out - h_in) — work input (positive)
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { propsFromPH_SI } from '../core/fluidAdapter';
import { solvePS } from 'iapws-if97';

export class NSCompressor implements NSComponent {
  id: string;
  type = 'compressor';
  params: Record<string, any>;

  constructor(id: string, params: {
    pressureRatio?: number;
    P_outlet?: number;     // Pa — fixed outlet pressure
    efficiency?: number;
  }) {
    this.id = id;
    this.params = { pressureRatio: 4, efficiency: 0.80, ...params };
  }

  applyBoundaryConditions(inlet: Junction, _outlet: Junction, _dt: number): ComponentUpdate {
    const { pressureRatio, P_outlet, efficiency } = this.params;

    const P_in = inlet.P;
    const h_in = inlet.h;
    const P_out = P_outlet ?? P_in * pressureRatio;

    try {
      const props_in = propsFromPH_SI(P_in, h_in);
      const s_in_kJkgK = props_in.s / 1000;
      const P_out_MPa = P_out / 1e6;

      const state_s = solvePS(P_out_MPa, s_in_kJkgK);
      const h_s = state_s.enthalpy * 1000; // kJ/kg → J/kg

      // Compressor: h_actual = h_in + (h_s - h_in) / η (divides by η)
      const h_actual = h_in + (h_s - h_in) / efficiency;
      const deltaH = h_actual - h_in; // Positive (enthalpy increases)
      const deltaP = P_out - P_in;    // Positive (pressure increases)

      // W_compressor is SPECIFIC work (J/kg = deltaH); stateSync multiplies by
      // the solved mass flow to report power (D-F2). Side-channel mirror per
      // the NSPump/NSTurbine _lastMetadata convention — never read by the solve.
      const metadata = { W_compressor: deltaH, P_out, h_out: h_actual };
      this.params._lastMetadata = metadata;

      return {
        deltaP,
        deltaH,
        momentumSource: 0,
        // Shaft-work telemetry only — the compression work already rides on
        // deltaH above, so the network deliberately does NOT consume this
        // (see the ComponentUpdate contract in types.ts).
        energySource: -deltaH,
        metadata,
      };
    } catch {
      const metadata = { W_compressor: 0 };
      this.params._lastMetadata = metadata;
      return {
        deltaP: P_out - P_in,
        deltaH: 0,
        momentumSource: 0,
        energySource: 0,
        metadata,
      };
    }
  }
}
